import axios, { AxiosInstance } from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * GET /api/v1/deposit/limits. Amounts are human-unit strings in the token's
 * own denomination, not raw lamports.
 */
export interface DepositLimits {
  minDeposit: string;
  maxDeposit: string;
  maxDailyVolume: string;
  currentDailyVolume: string;
  bridgeStatus: 'active' | 'paused';
  estimatedMintTime?: string;
  solanaConfirmations?: number;
  token?: string;
  splMint?: string;
  decimals?: number;
  /** True when the numbers are stale, or are static fallbacks. */
  degraded?: boolean;
  /**
   * Present only when real on-chain values were resolved, stale or not. Its
   * ABSENCE alongside `degraded` is the API saying it read nothing at all and
   * fell back to hardcoded defaults — defaults that are LOOSER than the live
   * caps (they advertise 100 SOL). Never show those as a limit.
   */
  limits?: { min?: unknown; max?: unknown; daily?: unknown };
  /** Present and false when the contract has this token switched off. */
  enabled?: boolean;
  warnings?: string[];
}

/** Whether a limits payload carries caps that were actually read on-chain. */
export function hasResolvedBand(
  limits: DepositLimits | null,
): limits is DepositLimits {
  return !!limits && limits.limits != null;
}

class BridgeApi {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async createDeposit(params: {
    sender: string;
    recipientDcc: string;
    amount: number;
    splMint?: string;
  }) {
    const { data } = await this.client.post('/deposit', params);
    return data;
  }

  async registerTransfer(params: {
    transferId: string;
    sender: string;
    recipient: string;
    amount: string;
    amountFormatted?: string;
    splMint?: string;
    sourceTxHash?: string;
    direction: 'sol_to_dcc' | 'dcc_to_sol';
  }) {
    try {
      const { data } = await this.client.post('/transfer/register', params);
      return data;
    } catch {
      // Non-critical — status polling still works via on-chain checks
    }
  }

  async createRedeem(params: {
    sender: string;
    solRecipient: string;
    amount: number;
    splMint?: string;
  }) {
    const { data } = await this.client.post('/redeem', params);
    return data;
  }

  async getTransfer(transferId: string) {
    const { data } = await this.client.get(`/transfer/${transferId}`);
    return data;
  }

  async getTransferHistory(address: string, page = 1, limit = 20) {
    const { data } = await this.client.get(`/transfer/history/${address}`, {
      params: { page, limit },
    });
    return data;
  }

  async getHealth() {
    const { data } = await this.client.get('/health');
    return data;
  }

  async getStats() {
    const { data } = await this.client.get('/stats');
    return data;
  }

  /**
   * Every SPL mint the DecentralChain bridge contract has registered and
   * enabled. 503 when the contract cannot be reached — callers must treat
   * that as "no SPL tokens", never as "no restriction".
   */
  async getTokens() {
    const { data } = await this.client.get('/tokens');
    return data as { count?: number; tokens?: unknown };
  }

  /**
   * The deposit band for one token, in that token's own units. Omit
   * `splMint` for native SOL. The `maxDeposit` here is the effective cap —
   * the smallest across the Solana program, the DCC contract and the daily
   * budget — so it is the number a user can actually get through, and it
   * moves whenever an operator changes it on-chain.
   */
  async getDepositLimits(splMint?: string) {
    const { data } = await this.client.get('/deposit/limits', {
      params: splMint ? { splMint } : undefined,
    });
    return data as DepositLimits;
  }

  async getRedeemLimits() {
    const { data } = await this.client.get('/redeem/limits');
    return data;
  }

  /**
   * Token metadata for a mintable token (DUSD, CR Stable).
   * `available` is false when that token's DCC contract is not deployed, in
   * which case its mint form must not be shown.
   */
  async getTokenInfo(path: string) {
    const { data } = await this.client.get(path);
    return data as { available?: boolean; contract?: string; [k: string]: any };
  }

  async getFees() {
    const { data } = await this.client.get('/fees');
    return data;
  }

  async getFeeQuote(amount: number, direction: 'deposit' | 'withdrawal') {
    const { data } = await this.client.get('/fees/quote', {
      params: { amount, direction },
    });
    return data;
  }
}

export const bridgeApi = new BridgeApi();
